/**
 * useRecordAgreementAcceptance — records one acceptance of the Independent
 * Contractor Agreement, and reports whether it landed.
 *
 * Both instructor onboarding wizards need this: `WalletSetupPage`, which is not
 * routed today, and the live screen at `/settings/instructor-tools/earnings`.
 * They had the same twenty-line mutation twice, which is how the two drift —
 * and this particular write is the one `requireAgreementAccepted` is
 * fail-closed on, so drifting it silently blocks Stripe onboarding.
 *
 * The architecture notes put repository calls, mutations and cache invalidation
 * in hooks rather than in components, and feature data access behind the
 * feature's repository. This is that hook.
 *
 * What the caller must respect: advance **only** in `onSuccess`. A step that
 * moves on after a failed insert is exactly the silent-loss defect the
 * acceptance table exists to fix. The mutation is deliberately not retried and
 * not deduplicated — the table is append-only history (AD-006), so confirming
 * twice is two rows, never a conflict, and double-clicks are prevented by
 * disabling the button while `isPending` is true.
 */

import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { ICA_VERSION } from '@/lib/agreementVersion';
import { walletRepository } from '@/features/wallet/repositories/supabaseWalletRepository';
import type { AgreementAcceptanceAction } from '@/features/wallet/repositories/types';

export interface UseRecordAgreementAcceptanceOptions {
  /**
   * Which affordance constituted acceptance. Named per surface so a stored row
   * traces back to the screen the instructor actually used.
   */
  acceptanceAction: AgreementAcceptanceAction;
  /** Runs only after the acceptance is recorded. */
  onSuccess: () => void;
}

export function useRecordAgreementAcceptance({
  acceptanceAction,
  onSuccess,
}: UseRecordAgreementAcceptanceOptions) {
  const { user } = useAuth();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error('You must be signed in to accept the agreement.');
      }

      await walletRepository.recordAgreementAcceptance({
        instructorId: user.id,
        agreementVersion: ICA_VERSION,
        acceptanceAction,
      });
    },
    onSuccess,
  });

  return { recordAcceptance: mutate, isRecordingAcceptance: isPending, acceptanceError: error };
}
